import dayjs from "dayjs";

const ONE_HOUR_MS = 60 * 60 * 1000;
const TWO_HOURS_MS = 2 * ONE_HOUR_MS;

/** Canonical YYYY-MM-DD for shift date keys. */
export function normalizeShiftDate(value) {
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

export function getShiftDates(shift) {
  const dates = Array.isArray(shift?.dates) ? shift.dates : [];
  const normalized = dates
    .map((entry) =>
      normalizeShiftDate(typeof entry === "string" ? entry : entry?.date)
    )
    .filter(Boolean);
  return [...new Set(normalized)];
}

export const ATTENDANCE_STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "checkIn", label: "Checked In" },
  { value: "checkOut", label: "Checked Out" },
  { value: "noShow", label: "Did not show" },
  { value: "overtime", label: "Overtime" },
];

export function parseAttendanceRecord(record) {
  const worker = record?.worker ?? {};
  const user = worker.user ?? {};
  const pi = worker.personalInformation ?? {};
  const logs = Array.isArray(record.attendanceLogs)
    ? record.attendanceLogs
    : [];
  const checkInLog = logs.find((log) => log.type === "check_in");
  const checkOutLog = logs.find((log) => log.type === "check_out");
  const fullName =
    user.fullName ||
    [pi.firstName, pi.surname].filter(Boolean).join(" ").trim() ||
    "Worker";

  return {
    id: record.id,
    fullName,
    phoneNumber: user.phoneNumber ?? null,
    checkInTime: checkInLog?.time ?? null,
    checkOutTime: checkOutLog?.time ?? null,
    attendanceStatus: record.attendanceStatus ?? null,
    hoursPerDay: record.hoursPerDay,
    date: normalizeShiftDate(record.date),
    shiftIndex: record.shiftIndex,
    profileUser: {
      ...user,
      fullName,
      photo: user.photo || pi.uploadSelfie,
      isVerified: user.isVerified ?? pi.isVerified,
      worker,
    },
  };
}

/** True when cached response matches the active request params (avoids stale rows on tab switch). */
export function attendanceResponseMatchesRequest(
  response,
  { bookingSlug, shiftIndex, date, filter }
) {
  if (!response) return false;
  if (response.bookingSlug != null && response.bookingSlug !== bookingSlug) {
    return false;
  }
  if (
    response.shiftIndex != null &&
    Number(response.shiftIndex) !== Number(shiftIndex)
  ) {
    return false;
  }
  if (
    response.date != null &&
    normalizeShiftDate(response.date) !== normalizeShiftDate(date)
  ) {
    return false;
  }
  if (response.filter != null && response.filter !== filter) {
    return false;
  }
  return true;
}

export function extractAttendanceRows(response) {
  const list = response?.data;
  if (!Array.isArray(list)) return [];
  return list.map(parseAttendanceRecord);
}

export function formatAttendanceDateTime(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, HH:mm") : "—";
}

export function formatTotalTime(checkIn, checkOut) {
  if (!checkIn || !checkOut) return "—";
  const start = dayjs(checkIn);
  const end = dayjs(checkOut);
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return "—";

  const totalMinutes = end.diff(start, "minute");
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

export function formatAttendanceTotalTime(row) {
  const fromLogs = formatTotalTime(row.checkInTime, row.checkOutTime);
  if (fromLogs !== "—") return fromLogs;

  const hours = Number(row.hoursPerDay);
  if (!Number.isFinite(hours) || hours <= 0) return "—";

  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  if (wholeHours > 0 && minutes > 0) return `${wholeHours}h ${minutes}m`;
  if (wholeHours > 0) return `${wholeHours}h`;
  return `${minutes}m`;
}

export function attendanceFilterKey(shiftIndex, date) {
  const normalized = normalizeShiftDate(date);
  if (normalized == null) return null;
  return `${shiftIndex}-${normalized}`;
}

/**
 * Shift start instant from date (YYYY-MM-DD) + API startTime (HH:mm).
 * timezone is accepted for API parity; parsed in local time until a tz plugin is added.
 */
export function buildShiftStartAt(date, startTime, _timezone) {
  const normalizedDate = normalizeShiftDate(date);
  if (!normalizedDate || !startTime) return null;
  const parsed = dayjs(`${normalizedDate} ${startTime}`, "YYYY-MM-DD HH:mm");
  return parsed.isValid() ? parsed : null;
}

export function extractAttendanceMeta(response) {
  const shift = response?.shift ?? {};
  return {
    totalWorkerCount: Number(response?.totalWorkerCount) || 0,
    totalWorkerCheckInCount: Number(response?.totalWorkerCheckInCount) || 0,
    hasDecisionTaken: shift.hasDecisionTaken === true,
    shiftStartTime: shift.startTime ?? null,
  };
}

/**
 * No-show decision window: show banner between 1h and 2h after shift start
 * when workers are missing and no decision has been taken.
 */
export function getNoShowBannerState({ shiftStartAt, now, meta }) {
  if (!shiftStartAt?.isValid?.() || !meta) {
    return {
      phase: "before",
      showBanner: false,
      msUntilOneHour: null,
      msUntilTwoHours: null,
    };
  }

  const nowMs = now?.valueOf?.() ?? dayjs(now).valueOf();
  const startMs = shiftStartAt.valueOf();
  const elapsedMs = nowMs - startMs;
  const msUntilOneHour = ONE_HOUR_MS - elapsedMs;
  const msUntilTwoHours = TWO_HOURS_MS - elapsedMs;

  if (elapsedMs < ONE_HOUR_MS) {
    return {
      phase: "before",
      showBanner: false,
      msUntilOneHour,
      msUntilTwoHours,
    };
  }

  if (elapsedMs >= TWO_HOURS_MS) {
    return {
      phase: "expired",
      showBanner: false,
      msUntilOneHour: 0,
      msUntilTwoHours: 0,
    };
  }

  const showBanner =
    !meta.hasDecisionTaken &&
    meta.totalWorkerCheckInCount < meta.totalWorkerCount;

  return {
    phase: "action",
    showBanner,
    msUntilOneHour: 0,
    msUntilTwoHours,
  };
}
